import {AbstractComponent} from "./abstract.component";
import {ElementRef, inject, Injectable, OnInit, Renderer2} from "@angular/core";
import {DEFAULT_PAGE_SIZE} from "@model";
import {Observable, of, Subject} from "rxjs";
import {LoaderService} from "@services";
import {debounceTime, map, switchMap, exhaustMap, catchError} from "rxjs/operators";
import {DOCUMENT} from "@angular/common";

@Injectable()
export abstract class PagingComponent<T> extends AbstractComponent implements OnInit {
    protected page = 1;
    protected items: T[] = [];
    protected hasMore = false;
    protected PAGE_SIZE = DEFAULT_PAGE_SIZE;
    protected showSpinner = true;

    protected isLoading = false;
    protected showFabButton: boolean = false;

    private explore$ = new Subject<void>();
    private loadMore$ = new Subject<void>();
    private isRunning = false;

    protected loaderService = inject(LoaderService);
    protected document = inject(DOCUMENT);

    private renderer = inject(Renderer2);
    private host = inject(ElementRef);

    private scrollButton!: HTMLElement;

    ngOnInit() {
        this.setupPagingHandler();
        this.createScrollToTopButton();
    }


    protected abstract fetchPage(page: number, loadMore?: boolean): Observable<T[]>;

    protected abstract afterSetItems(items: T[], loadMore?: boolean): void;
    protected afterOnExplore(): void {}

    private setupPagingHandler() {

        this.subscriptions.push(
            this.explore$.pipe(
                debounceTime(150),
                switchMap(() => {

                    this.isRunning = true;
                    this.isLoading = true;
                    this.page = 1;
                    this.loaderService.startLoading(this.showSpinner);

                    return this.fetchPage(this.page, false).pipe(
                        map(items => ({ items, loadMore: false })),
                        catchError(err => {
                            this.isRunning = false;
                            this.isLoading = false;
                            this.loaderService.dismissLoading();
                            // Optionally show a toast or log error here
                            this.handleExploreResult([], false);
                            return of({ items: [], loadMore: false });
                        })
                    );
                })
            )
                .subscribe(res => {
                    this.isRunning = false;
                    this.isLoading = false;
                    this.loaderService.dismissLoading();
                    this.handleExploreResult(res.items, false);
                })
        );

        this.subscriptions.push(
            this.loadMore$.pipe(
                exhaustMap(() => {

                    if (this.isRunning) {
                        return of(null);
                    }

                    this.isRunning = true;
                    this.isLoading = true;
                    this.loaderService.startLoading(this.showSpinner);

                    return this.fetchPage(this.page, true).pipe(
                        map(items => ({ items, loadMore: true })),
                        catchError(err => {
                            this.isRunning = false;
                            this.isLoading = false;
                            this.loaderService.dismissLoading();
                            // Optionally show a toast or log error here
                            this.handleExploreResult([], true);
                            return of({ items: [], loadMore: true });
                        })
                    );
                })
            )
                .subscribe(res => {
                    if (!res) return;

                    this.isRunning = false;
                    this.isLoading = false;
                    this.loaderService.dismissLoading();
                    this.handleExploreResult(res.items, true);
                })
        );
    }

    onExplore(loadMore = false): void {
        if (loadMore) {
            this.loadMore$.next();
        }
        else {
            this.explore$.next();
            this.afterOnExplore();
        }
    }

    loadMore(): void {
        if (this.hasMore) {
            this.loadMore$.next();
        }
    }

    handleExploreResult(items: T[], loadMore?: boolean): void {
        if (!items) items = [];
        this.hasMore = items.length > this.PAGE_SIZE;
        if (this.hasMore) {
            items = items.slice(0, this.PAGE_SIZE);
        }

        if (loadMore && this.items.length > 0) {
            items = [...this.items, ...items];
        }
        this.setListOfItems(items, loadMore);
    }

    setListOfItems(items: T[], loadMore?: boolean): void {
        this.items = items.length ? items : [];
        this.afterSetItems(this.items, loadMore);
        this.page++;
    }

    scrollToTop(): void {
        this.document.body.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'start'});
    }

    toggleFabButton(): void {
        if (!this.scrollButton) return;
        const visible = window.pageYOffset > 0 && this.items.length > 0;
        if (this.showFabButton !== visible) {
            this.showFabButton = visible;

            this.renderer.setStyle(
                this.scrollButton,
                'display',
                visible ? 'flex' : 'none'
            );
        }
    }


    private createScrollToTopButton(): void {
        const button = this.renderer.createElement('div');
        const icon = this.renderer.createElement('i');

        this.renderer.addClass(button, 'scroll-to-top');
        this.renderer.addClass(icon, 'bi');
        this.renderer.addClass(icon, 'bi-arrow-up');

        this.renderer.appendChild(button, icon);

        this.renderer.listen(button, 'click', () => {
            this.scrollToTop();
        });

        this.renderer.setStyle(button, 'display', 'none');

        this.renderer.appendChild(this.host.nativeElement, button);

        this.scrollButton = button;

        this.renderer.listen('window', 'scroll', () => {
           this.toggleFabButton();
        });
    }

}
